import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTypedDispatch } from '../state/store';
import {
    useGetNeoServersQuery,
    useGetMyNeoAccessQuery,
    useSetMyNeoAccessMutation,
    INeoServer,
} from '../state/neoServers';
import { neoMainApi } from '../state/neo';
import {
    getStoredNeoServer,
    setNeoServer,
    getStoredNeoAccessMap,
    setStoredNeoServerAccess,
} from '../utils/services/rqt-axios';
import { DEFAULT_TEAM } from '../consts/neoConstants';

/**
 * Loads the NEO server registry and owns the current selection: reconciling it
 * with the registry, persisting it, pointing the NEO client at it, and making
 * sure the access groups sent with NEO requests belong to that server.
 *
 * Access-group tokens are minted per NEO server, so switching servers without
 * switching groups makes the new server reject every token — the bootstrap
 * below fetches the selected server's own default group the first time it is
 * used and stores it per user, per server.
 */
/**
 * The registry is the only source of NEO addresses.
 *
 * There was a built-in fallback list here, resolving each server as a path
 * against REACT_APP_NEO_HTTP_API_URL. That is wrong on any deployment where NEO
 * is a separate host: the guessed URLs resolve to the static site, which answers
 * a POST with 405. Better to show that the list is unavailable than to send NEO
 * traffic somewhere that is not NEO.
 */
export const useNeoServerList = () => {
    const { data, isLoading, isError } = useGetNeoServersQuery(null);

    const servers = useMemo<INeoServer[]>(() => data?.servers ?? [], [data]);

    return { servers, isLoading, isError, isUnavailable: !isLoading && !servers.length };
};

export const useNeoServers = () => {
    const dispatch = useTypedDispatch();
    const { servers, isLoading, isError, isUnavailable } = useNeoServerList();
    const { data: accessData } = useGetMyNeoAccessQuery();
    const [saveNeoUserAccess] = useSetMyNeoAccessMutation();

    const [selectedId, setSelectedId] = useState<string>(() => getStoredNeoServer()?.id ?? '');

    // Mirror the stored map into localStorage, where the axios layer reads it
    // when building the Neo-Access-Groups header.
    useEffect(() => {
        if (!accessData?.access) {
            return;
        }

        Object.entries(accessData.access).forEach(([serverId, access]) => {
            setStoredNeoServerAccess(serverId, { groups: access.groups ?? [], teams: access.teams ?? [] });
        });
    }, [accessData]);

    // Reconcile once the registry arrives. A stored server that no longer exists
    // must not stay selected, and a stored url is re-resolved in case the
    // backend now reports a different host for the same id.
    useEffect(() => {
        if (!servers.length) {
            return;
        }

        const stored = getStoredNeoServer();
        const known = stored ? servers.find((server) => server.id === stored.id) : undefined;
        const next = known ?? servers.find((server) => server.default) ?? servers[0];

        if (!next) {
            return;
        }

        setNeoServer(next);

        setSelectedId((current) => (current === next.id ? current : next.id));
    }, [servers]);

    /**
     * Ask the now-selected NEO for its default access group and remember it for
     * this user. Failure is non-fatal: the request that follows will simply be
     * rejected by NEO the same way it is today, and the error surfaces there.
     */
    const bootstrapAccess = useCallback(
        async (server: INeoServer) => {
            if (getStoredNeoAccessMap()[server.id]?.groups?.length) {
                return;
            }

            try {
                const group = await dispatch(
                    neoMainApi.endpoints.getDefaultAccessGroup.initiate(null, { forceRefetch: true })
                ).unwrap();

                if (!group?.token) {
                    return;
                }

                const access = { groups: [group], teams: [DEFAULT_TEAM] };

                setStoredNeoServerAccess(server.id, access);

                await saveNeoUserAccess({ serverId: server.id, ...access }).unwrap();
            } catch (error) {
                console.error(`Could not load access groups for NEO server "${server.id}"`, error);
            }
        },
        [dispatch, saveNeoUserAccess]
    );

    const selectServer = useCallback(
        async (id: string) => {
            const server = servers.find((entry) => entry.id === id);

            if (!server || server.id === selectedId) {
                return;
            }

            setNeoServer(server);
            setSelectedId(server.id);

            // Before the reload, so the requests that follow it already carry
            // this server's groups.
            await bootstrapAccess(server);

            /**
             * Reload rather than resetting the RTK Query caches.
             *
             * `resetApiState()` empties the cache but does not re-trigger
             * queries that are already subscribed, so the open page kept
             * showing nothing until it was refreshed by hand. A reload also
             * settles the state that lives outside Redux and would otherwise
             * stay stale: `neoRtkAxios.defaults.baseURL`, set at module load,
             * and the access map the axios layer reads synchronously from
             * localStorage. Switching server is a deliberate, infrequent action
             * that changes every dataset on the page, so a full reload is
             * proportionate — and it is what users were doing manually anyway.
             */
            window.location.reload();
        },
        [servers, selectedId, bootstrapAccess]
    );

    const selected = useMemo(
        () => servers.find((server) => server.id === selectedId),
        [servers, selectedId]
    );

    return { servers, selected, selectedId, selectServer, isLoading, isError, isUnavailable };
};
